// Ribbon – jediné místo s příkazy okna (pravidla: návrh kap. 24).
//  • hlavičkový pruh VŽDY stejný: vlevo karty (plný ribbon) nebo malá tlačítka (kompaktní lišta), vpravo štítky filtrů,
//    hledání a šipka sbalení; okna typu Průzkumník mají adresní řádek (navigace + cesta) POD ribbonem (iw-addressbar)
//  • plný vs. kompaktní rozhoduje systém podle obsahu: ≥ 2 karty, ovládací prvky nebo > 6 tlačítek → plný
//  • karty: první pojmenuje okno (výchozí Domů), Zobrazení poslední ze systémových, vlastní mezi
//  • skupiny: systémové mají pevné pořadí mezi sebou, vlastní volné (order), Filtr poslední
//  • sbalení: šipka / dvojklik na ouško / Ctrl+F1, pamatováno pro všechna okna
//  • mobil: nikdy se nezalamuje – jeden posuvný řádek, filtr jako panel odspodu, hledání jako lupa
import { h, clear, onOutsideClick } from '../dom';
import { t, tk } from '../i18n';
import { icon } from '../icons';
import { IwMenu, type MenuItem } from './menu';

export interface RibbonButton {
  id: string;
  label: string;
  icon?: string | null;
  shortcut?: string | null;
  /** klíč karty: 'home' (výchozí), 'view', nebo vlastní */
  tab?: string;
  group?: string;
  /** pořadí vlastní skupiny (systémové mají dané) */
  order?: number;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
  toggle?: boolean;
  active?: boolean;
  menu?: MenuItem[];
  onClick?: () => void;
  /** Ovládací prvek místo tlačítka (filtr, výběr…). */
  control?: HTMLElement;
  /** Prvek skrytý za „Další filtry“. */
  overflow?: boolean;
}

export type RibbonMode = 'pc' | 'tablet' | 'mobile';

/** Pořadí systémových skupin mezi sebou; vlastní jdou mezi Správu a Filtr podle `order`. */
const SYSTEM_GROUP_ORDER: Record<string, number> = {
  'core.grp.nav': 0,
  'core.grp.new': 10,
  'core.grp.open': 20,
  'core.grp.edit': 30,
  'core.grp.clipboard': 40,
  'core.grp.organize': 50,
  'core.grp.manage': 60,
  'core.grp.actions': 70,
  'core.grp.view': 80,
  'core.ui.filters': 1000,
};
const CUSTOM_BASE = 500;
const COMPACT_MAX_BUTTONS = 6;

// ---- sbalení: po aplikacích (preference uživatele `ribbonCollapsed` = { '*': výchozí, [app]: bool }; v prohlížeči kopie pro start
// před přihlášením). Šipka a Ctrl+F1 platí pro všechna okna aplikace, dvojklik na kartu jen pro toto okno (bez uložení).
const COLLAPSE_KEY = 'iwan.ribbonCollapsed';
const listeners = new Set<() => void>();
let collapsedState: Record<string, boolean> = {};
const parseState = (v: unknown): Record<string, boolean> => (typeof v === 'boolean' ? { '*': v } : v && typeof v === 'object' && !Array.isArray(v) ? { ...(v as Record<string, boolean>) } : {});
try {
  const raw = localStorage.getItem(COLLAPSE_KEY);
  if (raw) collapsedState = parseState(raw === '1' ? true : raw === '0' ? false : JSON.parse(raw));
} catch {
  /* ignore */
}
let onCollapsedChanged: ((state: Record<string, boolean>) => void) | null = null;

function persist(silent: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsedState));
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
  if (!silent) onCollapsedChanged?.(collapsedState);
}

/** Stav z preferencí uživatele při přihlášení (starší hodnota bylo jedno true/false pro vše). */
export function setRibbonCollapsedState(v: unknown, silent = false): void {
  collapsedState = parseState(v);
  persist(silent);
}

/** Sbalení pro všechna okna jedné aplikace ('' = výchozí pro vše). */
export function setRibbonCollapsed(c: boolean, app = '', silent = false): void {
  collapsedState[app || '*'] = c;
  persist(silent);
}

export function isRibbonCollapsed(app = ''): boolean {
  return collapsedState[app || '*'] ?? collapsedState['*'] ?? false;
}

/** Shell zaregistruje uložení na server (preference uživatele). */
export function watchRibbonCollapsed(cb: (state: Record<string, boolean>) => void): void {
  onCollapsedChanged = cb;
}

/** Vysouvací panel pod tlačítkem. */
export function popover(anchor: HTMLElement, content: HTMLElement[]): HTMLElement {
  document.querySelector('.ribbon-popover')?.remove();
  const pop = h('div', { class: 'ribbon-popover' }, content);
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  pop.style.left = Math.max(4, Math.min(r.left, window.innerWidth - pop.offsetWidth - 8)) + 'px';
  pop.style.top = r.bottom + 4 + 'px';
  const close = () => {
    off();
    pop.remove();
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  const off = onOutsideClick(pop, close, anchor);
  document.addEventListener('keydown', onKey, true);
  return pop;
}

/** Panel odspodu (mobil): filtr, přetékající příkazy. */
export function bottomSheet(title: string, content: HTMLElement[]): HTMLElement {
  document.querySelector('.bottom-sheet')?.remove();
  const close = () => sheet.remove();
  const sheet = h(
    'div',
    { class: 'bottom-sheet', role: 'dialog', 'aria-label': title },
    h('div', { class: 'sheet-backdrop', onClick: close }),
    h('div', { class: 'sheet-panel' }, h('div', { class: 'sheet-head' }, h('span', null, title), h('button', { type: 'button', class: 'btn ghost sm', onClick: close }, icon('x'))), h('div', { class: 'sheet-body' }, content)),
  );
  document.body.appendChild(sheet);
  return sheet;
}

export class IwRibbon extends HTMLElement {
  private buttons: RibbonButton[] = [];
  private activeTab = '';
  private tabLabels: Record<string, string> = {};
  private tabsEl = h('div', { class: 'ribbon-tabs', role: 'tablist' });
  private barEl = h('div', { class: 'ribbon-bar' });
  private rightEl = h('div', { class: 'ribbon-right' });
  private bodyEl = h('div', { class: 'ribbon-body' });
  private collapseBtn = h('button', { type: 'button', class: 'ribbon-collapse' }, icon('chevron-down'));
  private searchToggle = h('button', { type: 'button', class: 'ribbon-search-toggle', title: t('core.cmd.search') }, icon('search'));
  private mode: RibbonMode = 'pc';
  private full = true;
  private forcedFull = false;
  private forcedCompact = false;
  private peek = false;
  private appId = '';
  private localCollapsed: boolean | null = null; // dvojklik na kartu: jen toto okno
  private collapsedNow(): boolean {
    return this.localCollapsed ?? isRibbonCollapsed(this.appId);
  }
  private onGlobal = () => { this.localCollapsed = null; this.applyCollapsed(this.collapsedNow()); };

  connectedCallback(): void {
    if (!this.hasChildNodes()) {
      this.append(h('div', { class: 'ribbon-head' }, this.tabsEl, this.barEl, this.rightEl, this.searchToggle, this.collapseBtn), this.bodyEl);
      this.appId = (this.closest('iw-window') as { app?: string } | null)?.app ?? '';
      this.collapseBtn.addEventListener('click', () => { this.localCollapsed = null; setRibbonCollapsed(!this.collapsedNow(), this.appId); });
      this.searchToggle.addEventListener('click', () => {
        this.classList.toggle('search-open');
        if (this.classList.contains('search-open')) {
          this.rightEl.querySelector<HTMLInputElement>('input')?.focus();
          const off = onOutsideClick(this.rightEl, () => {
            off();
            this.classList.remove('search-open');
          });
        }
      });
      this.applyCollapsed(this.collapsedNow());
    }
    listeners.add(this.onGlobal);
    this.render();
  }

  disconnectedCallback(): void {
    listeners.delete(this.onGlobal);
  }

  /** Prvky vpravo v hlavičce (štítky filtrů, hledání). */
  right(): HTMLElement {
    return this.rightEl;
  }

  /** Popisky karet: { home: 'ukazka.tab.deti' }; nezadané použijí core.ribbon.<klíč>. */
  setTabLabels(labels: Record<string, string>): void {
    this.tabLabels = labels;
    this.render();
  }

  /** Jeden řádek bez karet (seznam vložený v záložce záznamu – nad ním už je pás karet záznamu). */
  forceCompact(on = true): void {
    this.forcedCompact = on;
    this.render();
  }

  /** Aplikace může vynutit plný ribbon (konzistence v rámci aplikace). */
  forceFull(on = true): void {
    this.forcedFull = on;
    this.render();
  }

  setButtons(buttons: RibbonButton[]): void {
    this.buttons = buttons;
    const tabs = this.tabs();
    if (!tabs.includes(this.activeTab)) this.activeTab = tabs[0] ?? 'home';
    this.render();
  }

  update(id: string, patch: Partial<RibbonButton>): void {
    const b = this.buttons.find((x) => x.id === id);
    if (!b) return;
    Object.assign(b, patch);
    this.querySelectorAll<HTMLButtonElement>(`button[data-id="${id}"]`).forEach((el) => {
      el.disabled = Boolean(b.disabled);
      el.classList.toggle('active', Boolean(b.active));
    });
  }

  setDisabled(pred: (b: RibbonButton) => boolean): void {
    for (const b of this.buttons) if (!b.control) b.disabled = pred(b);
    this.querySelectorAll<HTMLButtonElement>('button[data-id]').forEach((el) => {
      const b = this.buttons.find((x) => x.id === el.dataset['id']);
      if (b && !b.control) el.disabled = Boolean(b.disabled);
    });
  }

  setMode(mode: RibbonMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.dataset['mode'] = mode;
    this.applyCollapsed(this.collapsedNow());
    this.render();
  }

  /** Zpětná kompatibilita. */
  setCollapsed(mobile: boolean): void {
    this.setMode(mobile ? 'mobile' : 'pc');
  }

  isFull(): boolean {
    return this.full;
  }

  private applyCollapsed(c: boolean): void {
    this.classList.toggle('collapsed', c && this.mode !== 'mobile' && this.full);
    this.collapseBtn.replaceChildren(icon(c ? 'chevron' : 'chevron-down'));
    this.collapseBtn.title = c ? t('core.ui.expand_ribbon') : t('core.ui.collapse_ribbon');
    this.endPeek();
  }

  private endPeek(): void {
    this.peek = false;
    this.classList.remove('peek');
  }

  private startPeek(): void {
    this.peek = true;
    this.classList.add('peek');
    const off = onOutsideClick(this, () => {
      off();
      this.endPeek();
    });
  }

  private tabs(): string[] {
    const out: string[] = [];
    for (const b of this.buttons) {
      const tab = b.tab ?? 'home';
      if (!out.includes(tab)) out.push(tab);
    }
    // prázdná karta se neukazuje (Přehled bez příkazů má jen Zobrazení); bez tlačítek vůbec zůstane Domů
    if (!out.length) out.push('home');
    return out.sort((a, b) => (a === 'home' ? -1 : b === 'home' ? 1 : a === 'view' ? 1 : b === 'view' ? -1 : 0));
  }

  private tabLabel(tab: string): string {
    const custom = this.tabLabels[tab];
    if (custom) return tk(custom);
    const key = 'core.ribbon.' + tab;
    const v = tk(key);
    return v === key ? tk(tab) : v;
  }

  private sortedGroups(list: RibbonButton[]): Map<string, RibbonButton[]> {
    const groups = new Map<string, RibbonButton[]>();
    for (const b of list) {
      const g = b.group ?? 'core.grp.actions';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)?.push(b);
    }
    // vlastní skupiny: order ≥ 0 → mezi Správu a Filtr; order < 0 → hned za Nový (např. navigace v čase u kalendáře)
    const weight = (g: string, items: RibbonButton[]) => {
      if (SYSTEM_GROUP_ORDER[g] !== undefined) return SYSTEM_GROUP_ORDER[g];
      const o = items[0]?.order ?? 0;
      return o < 0 ? (SYSTEM_GROUP_ORDER['core.grp.new'] ?? 10) + 1 + (o + 1000) / 10000 : CUSTOM_BASE + o;
    };
    return new Map(Array.from(groups.entries()).sort((a, b) => weight(a[0], a[1]) - weight(b[0], b[1])));
  }

  private decideFull(): boolean {
    if (this.forcedCompact) return false;
    if (this.forcedFull) return true;
    const tabs = this.tabs();
    if (tabs.length >= 2) return true;
    if (this.buttons.some((b) => b.control)) return true;
    return this.buttons.filter((b) => !b.control).length > COMPACT_MAX_BUTTONS;
  }

  private render(): void {
    if (!this.isConnected) return;
    clear(this.tabsEl);
    clear(this.barEl);
    clear(this.bodyEl);
    this.full = this.decideFull();
    const mobile = this.mode === 'mobile';
    this.classList.toggle('compact', !this.full);
    this.classList.toggle('mobile', mobile);
    const tabs = this.tabs();
    this.tabsEl.hidden = !this.full || (mobile && tabs.length <= 1);
    this.barEl.hidden = this.full;
    this.bodyEl.hidden = !this.full;
    this.collapseBtn.hidden = mobile || !this.full;
    this.searchToggle.hidden = !mobile || !this.rightEl.querySelector('.search-wrap');
    this.applyCollapsed(this.collapsedNow());

    if (this.full) {
      for (const tab of tabs) {
        const btn = h('button', { type: 'button', class: 'ribbon-tab' + (tab === this.activeTab ? ' active' : ''), role: 'tab', 'aria-selected': tab === this.activeTab ? 'true' : 'false' }, this.tabLabel(tab));
        btn.addEventListener('click', () => {
          const collapsed = this.classList.contains('collapsed');
          if (this.activeTab === tab) {
            if (collapsed) (this.peek ? this.endPeek() : this.startPeek());
            return;
          }
          this.activeTab = tab;
          this.render();
          if (collapsed) this.startPeek();
        });
        btn.addEventListener('dblclick', () => { this.localCollapsed = !this.collapsedNow(); this.applyCollapsed(this.collapsedNow()); });
        this.tabsEl.appendChild(btn);
      }
    }
    const visible = this.buttons.filter((b) => (b.tab ?? 'home') === this.activeTab);
    const groups = this.sortedGroups(visible);
    if (mobile) {
      this.renderRow(this.full ? this.bodyEl : this.barEl, groups, true);
      if (!this.full) this.barEl.hidden = false;
      return;
    }
    if (!this.full) {
      this.renderRow(this.barEl, groups, false);
      return;
    }
    for (const [g, list] of groups) {
      const label = tk(g);
      const redundant = list.length === 1 && list[0]?.label === label;
      const shown = list.filter((b) => !b.overflow);
      const hidden = list.filter((b) => b.overflow);
      const small = this.mode === 'tablet' ? false : list.length > 3 && !list.some((b) => b.control);
      const items = shown.map((b) => (b.control ? this.controlEl(b) : this.button(b, small)));
      if (hidden.length) {
        const more = h('button', { type: 'button', class: 'ribbon-btn small' }, icon('more'), h('span', { class: 'ribbon-label' }, tk('core.ui.more_filters')));
        more.addEventListener('click', () => popover(more, hidden.map((b) => (b.control ? this.controlEl(b) : this.button(b, true)))));
        items.push(more);
      }
      this.bodyEl.appendChild(h('div', { class: 'ribbon-group' + (list.some((b) => b.control) ? ' has-controls' : '') }, h('div', { class: 'ribbon-group-items' }, items), h('div', { class: 'ribbon-group-label' }, redundant ? '' : label)));
    }
  }

  /** Jeden řádek malých tlačítek (kompaktní lišta i mobil); skupiny oddělené čárkou; prvky filtru na mobilu do panelu. */
  private renderRow(target: HTMLElement, groups: Map<string, RibbonButton[]>, mobile: boolean): void {
    const row = h('div', { class: 'ribbon-row' });
    const controls: RibbonButton[] = [];
    let first = true;
    for (const [, list] of groups) {
      const buttons = list.filter((b) => !b.control);
      for (const b of list) if (b.control) controls.push(b);
      if (!buttons.length) continue;
      if (!first) row.appendChild(h('span', { class: 'ribbon-sep' }));
      first = false;
      const sorted = mobile ? [...buttons].sort((a, b) => Number(Boolean(b.primary)) - Number(Boolean(a.primary))) : buttons;
      for (const b of sorted) row.appendChild(this.button(b, true));
    }
    if (controls.length) {
      if (mobile) {
        const active = controls.filter((c) => this.controlHasValue(c)).length;
        const btn = h('button', { type: 'button', class: 'ribbon-btn small filter-btn' }, icon('filter'), h('span', { class: 'ribbon-label' }, tk('core.ui.filters')), active ? h('span', { class: 'badge count' }, String(active)) : null);
        btn.addEventListener('click', () => bottomSheet(tk('core.ui.filters'), controls.map((c) => this.controlEl(c))));
        row.prepend(btn);
      } else {
        for (const c of controls) row.appendChild(this.controlEl(c));
      }
    }
    target.appendChild(row);
  }

  private controlHasValue(b: RibbonButton): boolean {
    const el = b.control?.querySelector<HTMLInputElement | HTMLSelectElement>('input, select');
    return Boolean(el && el.value !== '');
  }

  private controlEl(b: RibbonButton): HTMLElement {
    return h('div', { class: 'ribbon-control', dataset: { id: b.id }, title: b.label }, b.control ?? null);
  }

  private button(b: RibbonButton, small: boolean): HTMLElement {
    const btn = h(
      'button',
      {
        type: 'button',
        class: 'ribbon-btn' + (small ? ' small' : '') + (b.primary ? ' primary' : '') + (b.danger ? ' danger' : '') + (b.active ? ' active' : ''),
        dataset: { id: b.id },
        title: b.shortcut ? `${b.label} (${b.shortcut})` : b.label,
        'aria-pressed': b.toggle ? (b.active ? 'true' : 'false') : null,
      },
      b.icon ? icon(b.icon, 'icon ribbon-icon') : null,
      h('span', { class: 'ribbon-label' }, b.label),
      b.menu ? icon('chevron-down', 'icon ribbon-caret') : null,
    );
    btn.disabled = Boolean(b.disabled);
    btn.addEventListener('click', () => {
      if (b.menu) IwMenu.showAt(b.menu, btn);
      else b.onClick?.();
    });
    return btn;
  }
}

customElements.define('iw-ribbon', IwRibbon);

// Ctrl+F1 sbalí/rozbalí ribbon aplikace aktivního okna (mimo okno: výchozí pro vše)
document.addEventListener('keydown', (e) => {
  if (e.key === 'F1' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    const app = (document.activeElement?.closest('iw-window') as { app?: string } | null)?.app ?? '';
    setRibbonCollapsed(!isRibbonCollapsed(app), app);
  }
});
